import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { AppState } from 'app/app.state';
import { AuthenticationService, IAuthResponse, isIAuthMfa } from 'app/core/services/authentication/authentication.service';
import { ILoginResponse } from 'app/core/services/authentication/models/login-response.interface';
import { ReCaptchaErrorCode } from 'app/core/services/authentication/models/recaptcha-site-verify-response.interface';
import { SpinnerService } from 'app/core/services/spinner/spinner.service';
import { StorageService } from 'app/core/services/storage/storage.service';
import { IShipmentException } from 'app/history/bir/models/shipment-exception.interface';
import { SessionIdleService } from 'app/session-idle/session-idle.service';
import { AccountLockedDialogService } from 'app/shared/components/account-locked-dialog/account-locked-dialog.service';
import { MfaEnabledDialogService } from 'app/shared/components/mfa-enabled-dialog/mfa-enabled-dialog.service';
import { MfaLearnMoreDialogService } from 'app/shared/components/mfa-learn-more-dialog/mfa-learn-more-dialog.service';
import { MfaSetupDialogService } from 'app/shared/components/mfa-setup-dialog/mfa-setup-dialog.service';
import { OTP_CONFIG } from 'app/shared/components/otp-dialog/config/otp.config';
import { OtpChannel } from 'app/shared/components/otp-dialog/enum/otp-channel.enum';
import { OtpDialogService } from 'app/shared/components/otp-dialog/otp-dialog.service';
import { IOtpAttempts, OtpResponseType, OtpService } from 'app/shared/components/otp-dialog/services/otp.service';
import { Customer } from 'app/shared/models/customer/customer.model';
import { NotificationType } from 'app/shared/models/notification-type';
import { BirService } from 'app/shared/services/bir/bir.service';
import { CustomerService } from 'app/shared/services/customer/customer.service';
import { DeviceTokenService } from 'app/shared/services/device-token.service';
import { ErrorHandlerService } from 'app/shared/services/error-handler/error-handler.service';
import { NotificationService } from 'app/shared/services/notification/notification.service';
import { OnboardingService } from 'app/shared/services/onboarding/onboarding.service';
import { TranslationService } from 'app/shared/services/translation/translation.service';
import { User } from 'app/shared/services/user/models/user.model';
import { UtilityService } from 'app/shared/services/utility/utility.service';
import { SupportScriptService } from 'app/support-scripts/support-script.service';
import { TermsDialogService } from 'app/terms/dialog/terms-dialog.service';
import { TermsService } from 'app/terms/services/terms.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { environment } from 'environments/environment';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { EMPTY, filter, forkJoin, Observable, of, Subject, switchMap } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { LoginFooterService } from './login-footer/login-footer.service';
import { ILoginFooterContent } from './login-footer/models/login-footer-content.interface';
import { UniversalLoginService } from '../services/universal-login.service';
import { EmailVerificationDialogService } from '../email-verification-dialog/email-verification-dialog.service';

dayjs.extend(utc);

interface ILoginForm {
    userName: string;
    password: string;
    isRememberMe: boolean;
}

@Component({
    selector: 'upsc-login',
    templateUrl: './login-jwt.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
    @ViewChild('failedLogin') public failedLogin: ElementRef;
    public redirect: string;
    public user: User;
    public formGroup: UntypedFormGroup;
    public isInProgress = false;
    public shouldShowSignUp = true;
    public isEUUser: boolean = false;
    public footerContent$: Observable<ILoginFooterContent>;
    public isParcelProDomain = false;
    public isAuth0Enabled = false;

    private currentLanguage = 'en';
    private readonly otpId: string;
    private readonly otp: string;

    private ngDestroyed$ = new Subject<void>();

    public constructor(private readonly formBuilder: UntypedFormBuilder,
                       private readonly authenticationService: AuthenticationService,
                       private readonly storageService: StorageService,
                       private readonly storage: StorageMap,
                       private readonly customerService: CustomerService,
                       private notificationService: NotificationService,
                       private birService: BirService,
                       private spinnerService: SpinnerService,
                       private utilityService: UtilityService,
                       private router: Router,
                       private errorHandlerService: ErrorHandlerService,
                       private translationService: TranslationService,
                       private route: ActivatedRoute,
                       private readonly supportScriptService: SupportScriptService,
                       private readonly loginFooterService: LoginFooterService,
                       private readonly termsService: TermsService,
                       private readonly termsDialogService: TermsDialogService,
                       private readonly recaptchaService: ReCaptchaV3Service,
                       private readonly sessionIdleService: SessionIdleService,
                       private readonly mfaLearnMoreDialogService: MfaLearnMoreDialogService,
                       private readonly mfaSetupDialogService: MfaSetupDialogService,
                       private readonly mfaEnabledDialogService: MfaEnabledDialogService,
                       private readonly otpService: OtpService,
                       private readonly otpDialogService: OtpDialogService,
                       private readonly deviceTokenService: DeviceTokenService,
                       private readonly accountLockedDialogService: AccountLockedDialogService,
                       private readonly appState: AppState,
                       private readonly universalLoginService: UniversalLoginService,
                       private readonly emailVerificationDialogService: EmailVerificationDialogService,
                       private readonly onboardingService: OnboardingService,
    ) {
        this.sessionIdleService.clearAll();
        this.otpId = this.route.snapshot.queryParamMap.get('otpId');
        this.otp = this.route.snapshot.queryParamMap.get('otp');
        this.isParcelProDomain = !this.utilityService.isEUDomainName();

        // this.mfaLearnMoreDialogService.open();
        // this.mfaSetupDialogService.open();
        // this.otpDialogService.open({
        //     channel: OtpChannel.EMAIL,
        //     destination: null,
        //     length: OTP_CONFIG.otpLength,
        //     context: 'login',
        //     expirySeconds: 0,
        //     prefill: {
        //         otpId: null,
        //         otp: null,
        //     },
        // });
        // this.accountLockedDialogService.open();
        // this.mfaEnabledDialogService.open();
    }

    public get supportTel(): string {
        const currentDomainName = this.utilityService.getCurrentDomainName();
        switch (true) {
            case currentDomainName.includes('upscapitalde.com'):
                return '+49 69 6630 8330';
            case currentDomainName.includes('upscapitaluk.com'):
                return '+44 20 7949 0144';
            case currentDomainName.includes('upscapitalfr.com'):
                return '+33 1 73 01 84 55';
            case currentDomainName.includes('upscapitalit.com'):
                return '+39 02 4527 9099';
            default:
                return '+1 (888) 683-2300';
        }
    }

    public get contactUsLink(): string {
        const currentDomainName = this.utilityService.getCurrentDomainName();
        switch (true) {
            case currentDomainName.includes('upscapitalde.com'):
                return 'https://www.parcelpro.com/de/de/about-us/contact-us.html';
            case currentDomainName.includes('upscapitaluk.com'):
                return 'https://www.parcelpro.com/gb/en/about-us/contact-us.html';
            case currentDomainName.includes('upscapitalfr.com'):
                return 'https://www.parcelpro.com/fr/fr/about-us/contact-us.html';
            case currentDomainName.includes('upscapitalit.com'):
                return 'https://www.parcelpro.com/it/it/about-us/contact-us.html';
            default:
                return '/contact-us';
        }
    }

    public getCountryFromDomain(): string {
        const currentDomainName = this.utilityService.getCurrentDomainName();
        switch (true) {
            case currentDomainName.includes('upscapitalde.com'):
                return 'de';
            case currentDomainName.includes('upscapitaluk.com'):
                return 'uk';
            case currentDomainName.includes('upscapitalfr.com'):
                return 'fr';
            case currentDomainName.includes('upscapitalit.com'):
                return 'it';
            default:
                return 'us';
        }
    }

    public onSubmit(event, form: ILoginForm): void {
        event.preventDefault();

        if (this.isAuth0Enabled) {
            if (this.formGroup.controls.userName.valid && !this.isInProgress) {
                void this.startFreshAuth0Login(form.userName);
            }

            return;
        }

        if (!form.userName || !form.password) {
            return;
        }

        if (!environment.feature.reCAPTCHA) {
            this.login(form);
            return;
        }

        this.recaptchaService.execute('login')
            .pipe(
                switchMap((token) => this.authenticationService.verifyReCaptcha(token)),
            )
            .subscribe(
                (res) => {
                    if (!res.success) {
                        this.notificationService.notify(
                            'Verification failed. If the problem persists, please contact support.',
                            'Error verifying reCAPTCHA',
                            NotificationType.ERROR,
                        );

                        for (const err of res['error-codes']) {
                            const errorDescription = ReCaptchaErrorCode[err];
                            if (!errorDescription) {
                                return;
                            }

                            console.error(err, errorDescription);
                        }

                        return;
                    }

                    this.login(form);
                },
            );
    }

    private async startFreshAuth0Login(username: string): Promise<void> {
        this.isInProgress = true;
        const returnTo = this.authenticationService.redirectUrl || '/dashboard';
        const country = this.getCountryFromDomain();
        try {
            await this.authenticationService.logout(false);
            await this.universalLoginService.startFreshLogin(username, country, returnTo);
        } catch {
            this.handleAuth0StartError();
        }
    }

    private handleAuth0StartError(): void {
        this.isInProgress = false;
        console.error('[Auth0] Login could not be started.');
        this.notificationService.notify(
            'Unable to start sign-in. Please try again or contact support.',
            'Authentication Error',
            NotificationType.ERROR,
        );
    }

    public ngOnInit(): void {
        this.isAuth0Enabled = this.universalLoginService.isAuth0Enabled();
        if (this.isAuth0Enabled) {
            this.isInProgress = true;
            void this.universalLoginService.resumeLoginAfterLogout()
                .then((resuming) => {
                    this.isInProgress = resuming;
                })
                .catch(() => this.handleAuth0StartError());
        }
        
        this.formGroup = this.formBuilder.group({
            userName: ['', Validators.compose(this.isAuth0Enabled
                ? [Validators.required, Validators.email]
                : [Validators.required, Validators.maxLength(30)])],
            password: ['', Validators.compose([
                Validators.required, 
                Validators.maxLength(100),
            ])],
            isRememberMe: [false],
        });

        if (this.otpId) {
            this.formGroup.controls.userName.setValue(this.otpId);
        }

        this.supportScriptService.loadDefaultScript();

        const lang = this.appState.language$() || this.utilityService.getSiteLanguage() || 'en';
        this.currentLanguage = lang ?? this.utilityService.getSiteLanguage();
        this.isEUUser = ['DE', 'EN-GB', 'IT', 'FR'].includes(this.currentLanguage.toUpperCase());
        this.shouldShowSignUp = !['de', 'en-gb', 'fr', 'it'].includes(lang);
        this.footerContent$ = this.loginFooterService.getFooterContent(lang);
        this.translationService.changeLanguage(this.currentLanguage);

        this.storage.get('lang')
            .pipe(
                map((lang: string) => lang || this.utilityService.getSiteLanguage() || 'en'),
            )
            .subscribe(
                (lang) => {
                    this.currentLanguage = lang ?? this.utilityService.getSiteLanguage();
                    this.isEUUser = ['DE', 'EN-GB', 'IT', 'FR'].includes(this.currentLanguage.toUpperCase());

                    this.shouldShowSignUp = !['de', 'en-gb', 'fr', 'it'].includes(lang);
                    this.footerContent$ = this.loginFooterService.getFooterContent(lang);

                    this.translationService.changeLanguage(this.currentLanguage);
                },
            );

    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    private requireOtp(): Observable<boolean | OtpResponseType> {
        return this.otpDialogService.open({
            channel: OtpChannel.EMAIL,
            destination: null,
            length: OTP_CONFIG.otpLength,
            context: 'login',
            expirySeconds: 0,
            prefill: {
                otpId: this.formGroup?.controls.userName?.value,
                otp: null,
            },
        });
    }

    private login(form: ILoginForm): void {
        if (this.isInProgress || !form) {
            return;
        }

        this.isInProgress = true;
        this.spinnerService.show();

      const isDeviceTokenPerUser = environment.feature.deviceToken?.perUser;
      const login$ =
        this.deviceTokenService.getOrCreate$(isDeviceTokenPerUser ? form.userName : null)
            .pipe(
              switchMap((token) => {
                return environment.feature.coreAuth
                  ? this.authenticationService.secureLogin(form.userName, form.password, form.isRememberMe, token)
                  : this.authenticationService.login(form.userName, form.password, form.isRememberMe, token);
              }),
            );

        login$
            .pipe(
                catchError(err => {
                    console.error(err);
                    const isAuthMfaPending = (err) => {
                        const authResponse = err.error;
                        return authResponse !== null && authResponse !== undefined
                            && typeof authResponse.delay === 'string'
                            && typeof authResponse.error === 'string';
                    };

                    if (isAuthMfaPending(err)) {
                        this.spinnerService.hide();
                        this.otpDialogService.open({
                            channel: OtpChannel.EMAIL,
                            destination: null,
                            length: 6,
                            context: 'login',
                            expirySeconds: 0,
                            prefill: {
                                otpId: form.userName,
                                otp: null,
                            },
                        });

                        return of(null);
                    }

                    const isAccountLocked = (err: any) => {
                        const authResponse = err.error;
                        return authResponse !== null && authResponse !== undefined
                            && typeof authResponse.error === 'string'
                            && typeof authResponse.lockoutEndUtc === 'string';
                    };

                    if (isAccountLocked(err)) {
                        this.spinnerService.hide();
                        this.accountLockedDialogService.open({
                            lockoutEndUtc: err.error?.lockoutEndUtc,
                        });

                        // this.notificationService.notify(
                        //     'Your account is locked. Please contact support.',
                        //     'Account Locked',
                        //     NotificationType.ERROR,
                        // );

                        this.otpService.clearOtpAttempts(form.userName).subscribe();

                        return of(null);
                    }

                    return this.handleJwtLoginFailure(err);
                }),
                takeUntil(this.ngDestroyed$),
                finalize(() => {
                    this.isInProgress = false;
                    this.spinnerService.hide();
                }),
                filter(res => !!res),
                switchMap(res => {
                    if (!environment.feature.mfa.enabled) {
                        return of(res as ILoginResponse);
                    }

                    const otpRequired = isIAuthMfa(res) && res?.mfa_required;
                    if (!otpRequired) {
                        return this.authenticationService.handleAuthenticationSuccessResponse((res as ILoginResponse).auth as IAuthResponse, form.isRememberMe);
                    }

                    if (OTP_CONFIG.resetOtpAttemptsOnGoodCredentials) {
                        this.otpService.clearOtpAttempts(form.userName).subscribe();
                    }

                    const otpAttemptStorage$ = OTP_CONFIG.resetOtpAttemptsOnGoodCredentials
                        ? this.otpService.clearOtpAttempts(form.userName)
                              .pipe(
                                  switchMap(() => this.otpService.setMaxOtpAttempts(form.userName, res?.otp_maxfails)),
                              )
                        : this.otpService.setMaxOtpAttempts(form.userName, res?.otp_maxfails);

                    this.spinnerService.hide();
                    return otpAttemptStorage$
                        .pipe(
                            switchMap((res) => {
                                return this.otpService.getOtpAttempts(form.userName)
                                           .pipe(
                                               switchMap((res) => {
                                                   if (!res || (!res?.attempts && !res?.maxAttempts)) {
                                                       return of({
                                                           attempts: 0,
                                                           maxAttempts: 0,
                                                       } as IOtpAttempts);
                                                   }

                                                   if ((res?.attempts || 0) > (res?.maxAttempts || 0)) {
                                                       return this.otpService.resetOtpAttempts(form.userName)
                                                                  .pipe(
                                                                      map(() => res),
                                                                  );
                                                   }

                                                   return of(res);
                                               }),
                                           );
                            }),
                            switchMap((otpAttempts: IOtpAttempts) => {
                                return this.otpDialogService.open({
                                    channel: OtpChannel.EMAIL,
                                    destination: null,
                                    length: OTP_CONFIG.otpLength,
                                    context: 'login',
                                    expirySeconds: res.otp_expire * 60,
                                    prefill: {
                                        otpId: this.formGroup?.controls.userName?.value,
                                        otp: null,
                                    },
                                }).pipe(
                                    switchMap(
                                        (res) => {
                                            if ((res as any)?.access_token) {
                                                this.otpDialogService.close();
                                                return this.authenticationService.handleAuthenticationSuccessResponse(res as IAuthResponse, form.isRememberMe);
                                            }

                                            return EMPTY;
                                        },
                                    ),
                                );
                            }),
                        );
                }),
                filter(res => !!res),
                tap((res) => {
                    this.onboardingService.setFromLoginCounter(res?.user?.LoginNotificationCount ?? 3);
                    this.sessionIdleService.simulateInitialActivity();
                    this.spinnerService.show();
                    this.setCurrentLanguage(res?.customer).subscribe();
                    this.handleUserInfo(res?.user);
                    this.handleCustomerInfo(res?.customer);

                    if (!res?.user?.IsFirstLogon) {
                        this.checkProductTermsConditions();
                    }
                }),
                tap((res: ILoginResponse) => {
                    if (res?.verify_email === true || res?.auth?.verify_email === true) {
                        this.emailVerificationDialogService.open();
                    }

                }),
                switchMap((res) => {
                    if (res?.customer?.Status === 11) {
                        this.router.navigate(['/account-suspended']).then();
                        return EMPTY;
                    }

                    return of(res);
                }),
                filter(res => !!res),
                switchMap(
                    (res: ILoginResponse) => {
                        return this.birService.getShipmentExceptions()
                                   .pipe(
                                       catchError(err => this.handleBIRError(err)),
                                       map((exceptions) => ({
                                           loginResponse: res,
                                           shipmentExceptions: exceptions,
                                       })),
                                   );
                    },
                ),
            )
            .subscribe(
                async (res: { loginResponse: ILoginResponse, shipmentExceptions: IShipmentException[] }) => {
                    this.otpService.clearOtpAttempts(form.userName).subscribe();

                    if (res?.shipmentExceptions?.length) {
                        await this.router.navigate(['/bir']);
                        return;
                    }

                    await this.navigate();
                },
            );
    }

    private handleJwtLoginFailure(err): Observable<never> {
        this.spinnerService.hide();
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            'Failed logging in',
            NotificationType.ERROR);
        this.isInProgress = false;

        return EMPTY;
    }

    /*
        private handleGetUserSuccess(user) {
            const currentUser = {
                username: user.UserName,
                sessionid: user.MobileSessionID,
                defaultLocation: user.DefaultLocation,
                isAdmin: user.IsAdmin,
                isGuest: user.IsGuest,
                userId: user.UserId,
            };

            this.user = user;

            // sessionStorage.setItem('currentuser', JSON.stringify(currentUser));
            // this.storage.set('currentuser', currentUser).subscribe(() => {});
            this.sharedService.userSubject.next(user);

            this.utilityService.clearSubscriptions([this.getCustomerSubscription]);
            this.getCustomerSubscription
                = this.userService.getCustomer()
                      .pipe(
                          catchError((err) => {
                              this.notificationService.notify(
                                  this.errorHandlerService.getHttpErrorMessage(err),
                                  'Failed Getting Customer',
                                  NotificationType.ERROR);

                              return of(null);
                          }),
                      )
                      .subscribe(
                          async (customer) => {
                              if (!customer) {
                                  return;
                              }

                              this.sharedService.customerSubject.next(customer);
                              this.userRouteService.setUserGuards();

                              if (customer.Status === 11) {
                                  await this.router.navigate(['/account-suspended']);
                                  return;
                              }

                              // await this.handleFirstLogon();
                              // this.betaFeatureService.checkBetaPreferences(customer?.CustomerId);
                              this.checkBIR();
                              this.setCurrentLanguage(customer);
                          },
                      );

            this.supportScriptService.loadUserSupportScript(user);

            // this.isInProgress = false;
        }
    */

    private handleUserInfo(user: User): void {
        this.user = user;
        // this.sharedService.userSubject.next(user);
        this.supportScriptService.loadUserSupportScript(user);
    }

    private handleCustomerInfo(customer: Customer): void {
        // this.sharedService.customerSubject.next(customer);
    }

    private checkProductTermsConditions(): void {
        this.termsService.getCustomerProductTermsConditions()
            .pipe(
                catchError((err) => {
                    this.notificationService.notify(
                        err?.Message,
                        'Failed Saving Data',
                        NotificationType.ERROR,
                    );

                    return EMPTY;
                }),
                filter(terms => !!terms?.length),
                switchMap(terms => {
                    return this.termsDialogService.open(terms[0])
                               .pipe(
                                   map(isAccepted => ({ isAccepted, latestTerms: terms[0] })),
                               );
                }),
                filter(res => !!res?.isAccepted),
                map((res) => ({ latestTerms: res.latestTerms })),
                switchMap(res => this.termsService.saveCustomerProductTermsConditions(res?.latestTerms?.productTermsId)),
            )
            .subscribe(
                (res) => {
                    if (res) {
                        return;
                    }

                    this.notificationService.notify(
                        'Failed saving terms acceptance. Please contact administrator.',
                        'Failed Saving Data',
                        NotificationType.ERROR,
                    );
                },
            );
    }

    private setCurrentLanguage(customer: Customer): Observable<unknown> {
        const customerLanguage = this.customerService.getCustomerLanguage(customer);
        this.appState.language$.set(customerLanguage);

        return forkJoin([
            this.storage.set('lang', customerLanguage),
            this.storage.set('site-locale', customerLanguage),
        ]).pipe(
            tap(() => {
                this.storageService.set('lang', customerLanguage);
                this.storageService.set('site-locale', customerLanguage);
                this.translationService.changeLanguage(customerLanguage);
            }),
        );
    }

    private handleBIRError(err): Observable<never> {
        console.error('Error Fetching BIR Exceptions', err);
        this.router.navigate(['dashboard']).then();
        return EMPTY;
    }

    private async navigate(): Promise<void> {
        this.redirect = this.route.snapshot.fragment;
        if (this.redirect) {
            await this.router.navigate([this.redirect]);
        } else if (this.authenticationService.redirectUrl) {
            await this.router.navigate([this.authenticationService.redirectUrl]);
            this.authenticationService.redirectUrl = null;
        } else {
            await this.router.navigate(['dashboard']);
        }
    }
}
